// src/admin/lib/buyLinks.js
//
// Ticket 118. Everything the admin's buy-links editor reads and writes, in one file.
//
// A book's buy links live in two places: the purchase_links TABLE (where the first bulk
// import landed) and the books.purchase_links COLUMN (where the author app writes). The book
// page reads both through getPurchaseLinks, which hands them to visibleBuyLinks. This file
// uses the same function to say which links readers see, so the editor and the book page
// cannot disagree.
//
// This is the ONE admin file allowed to read the purchase_links table (scripts/check-book-
// link-parity.mjs names it). Every other admin screen gets its numbers from the database
// functions: counting this table's rows says a book can be bought when readers see no button.
// The editor reads the rows to show them and to remove them, never to count them.
//
// Wording: approved 29 Oct, word for word (weekly ticket 118). The five reasons are the ones
// the author's form gives (reanlo-author src/lib/bookLinks.js, bookLinkWarning) with "your"
// taken out.

import { supabase } from '../../lib/supabase';
import { logAudit } from './auditLog';
import { isBookLink, visibleBuyLinks } from '../../lib/retailers';

// The seven stores on the author's form (reanlo-author src/pages/AddBook.jsx, RETAILERS), spelled
// the same, so the author's form shows the right store when they next edit the book.
export const BUY_STORES = ['Amazon', 'Barnes & Noble', 'Kobo', 'Bookshop.org', 'Apple Books', 'Books-A-Million', 'Indigo'];

export const LINK_WORDS = {
  readersSee: 'readers see this',
  readersDont: reason => `readers don't see this: ${reason}`,
  sameAddress: 'same address as a link above, so readers only get that one',
  fromImport: 'from the first import',
  alreadyHas: 'This book already has that address.',
  saved: 'Saved.',
  notSaved: error => `Not saved, nothing was changed: ${error}`,
  changedUnderneath: "This book's links changed while you had it open, so nothing was saved. Here they are now.",
  removeConfirm: 'Remove this link from the book?',
  notRemoved: error => `Not removed, it is still on the book: ${error}`,
  notOnEitherTab: status => `This book isn't on either tab: its status is ${status}.`,
  noSuchBook: 'No book has that id.',
  notSavedYet: 'not saved yet',
  closeAnyway: "These changes aren't saved. Close anyway?",
};

function hostOf(url) {
  try {
    const h = new URL(url).hostname.toLowerCase();
    return h.startsWith('www.') ? h.slice(4) : h;
  } catch {
    return null;
  }
}

function pathOf(url) {
  try { return new URL(url).pathname; } catch { return ''; }
}

// Why readers don't see a link, or null when it is a page for the book. Same decision as
// isBookLink (retailers.js), so a link is given a reason exactly when isBookLink refuses it.
export function buyLinkReason(retailer, url) {
  if (isBookLink(retailer, url)) return null;
  const host = hostOf(url);
  const path = pathOf(url);
  if (host && host.startsWith('amazon.')) {
    if (/\/author\//i.test(path) || /\/stores\//i.test(path)) {
      return 'This is an Amazon author page, not a book page. Open the book itself on Amazon and copy that address, it will have /dp/ in it.';
    }
    if (/\/product-reviews\//i.test(path)) {
      return 'This is the reviews page, not the book page. Copy the address of the book itself, it will have /dp/ in it.';
    }
    return 'This looks like an Amazon search rather than a book page. Open the book and copy that address, it will have /dp/ in it.';
  }
  if (/[?&](k|q|query|keywords)=/i.test(url) || /\/(s|search|search2|beta-search)(\/|\?|$)/i.test(path)) {
    return 'This is a search results page, not a book page. Open the book from those results and copy that address instead.';
  }
  return 'This does not look like a link to the book itself. Open the book on the retailer and copy the address from there.';
}

// The key visibleBuyLinks uses to tell two copies of one address apart.
export const addressKey = url => String(url ?? '').trim().toLowerCase();

// What the editor says about every link, in the order the book page reads them: the table's
// links (in id order), then the column's. `visible` is decided by visibleBuyLinks itself.
//   status 'visible'  readers see it
//   status 'same'     an earlier link has this address, so readers only get that one
//   status 'hidden'   readers don't see it, with the reason
export function describeLinks(tableLinks, columnLinks) {
  const visible = new Set(visibleBuyLinks(tableLinks, columnLinks));
  const seen = new Set();
  return [...tableLinks.map(link => ({ link, source: 'table' })), ...columnLinks.map(link => ({ link, source: 'column' }))]
    .map(item => {
      const key = item.link?.url ? addressKey(item.link.url) : null;
      const repeat = key !== null && seen.has(key);
      if (key !== null) seen.add(key);
      if (visible.has(item.link)) return { ...item, status: 'visible', reason: null };
      if (repeat) return { ...item, status: 'same', reason: null };
      return { ...item, status: 'hidden', reason: buyLinkReason(item.link?.retailer, item.link?.url) };
    });
}

// A link about to be added or changed: why it can't be, or null. `others` is every other
// link the book has (table and column), so an address the book already has is refused.
export function newLinkProblem(link, others) {
  const reason = buyLinkReason(link.retailer, link.url);
  if (reason) return reason;
  const key = addressKey(link.url);
  if (others.some(o => o?.url && addressKey(o.url) === key)) return LINK_WORDS.alreadyHas;
  return null;
}

// A link with its address trimmed. Every address is trimmed before it is compared and before it is
// written, so a stray space can't make a save that changes nothing, or sit in the book's list.
export const trimmed = l => ({ ...l, url: String(l?.url ?? '').trim() });

const asList = x => (Array.isArray(x) ? x : []);
const asLinks = x => asList(x).filter(l => l && typeof l === 'object');
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Both places, for one book. A failed read is an error, never "no links". A column that isn't a
// list reads as no links, as getPurchaseLinks reads it.
export async function readBookLinks(bookId) {
  const [t, b] = await Promise.all([
    supabase.from('purchase_links').select('id, retailer, url').eq('book_id', bookId).order('id'),
    supabase.from('books').select('purchase_links').eq('id', bookId).maybeSingle(),
  ]);
  if (t.error) return { error: t.error.message };
  if (b.error) return { error: b.error.message };
  if (!b.data) return { error: LINK_WORDS.noSuchBook };
  return { error: null, tableLinks: t.data || [], column: asLinks(b.data.purchase_links), rawColumn: asList(b.data.purchase_links) };
}

const pair = l => `${l.retailer} ${String(l.url).trim()}`;

// The links in `a` that `b` does not have as many of (a repeated link counts once per copy).
function surplus(a, b) {
  const have = new Map();
  b.forEach(l => have.set(pair(l), (have.get(pair(l)) || 0) + 1));
  const out = [];
  a.forEach(l => {
    const k = pair(l), n = have.get(k) || 0;
    if (n > 0) have.set(k, n - 1); else out.push(k);
  });
  return out;
}

// Whether two lists of links differ, by the same comparison the audit line uses: store and trimmed address,
// order ignored. Save is on exactly when this is true, so the audit line can never come out empty (a link
// taken off and put back leaves the lists the same).
export function linksDiffer(before, after) {
  const a = asLinks(before).map(trimmed), b = asLinks(after).map(trimmed);
  return surplus(a, b).length > 0 || surplus(b, a).length > 0;
}

// The audit line for a save: which links went on and which came off. A save is only possible when
// linksDiffer is true, so at least one of the two is never empty.
function saveAuditText(title, bookId, before, after) {
  const added = surplus(after, before);
  const removed = surplus(before, after);
  const parts = [];
  if (added.length) parts.push(`added ${added.join(', ')}`);
  if (removed.length) parts.push(`took off ${removed.join(', ')}`);
  const text = `Buy links saved on "${title}" (book ${bookId}): ${parts.join('; ')}`;
  return text.length > 600 ? `${text.slice(0, 597)}...` : text;
}

// Save the column's links as shown. Reads the column again first: if it is not what the editor
// opened with, nothing is written and the fresh list comes back. The write asks for the row
// back, and no row back is a failure. The audit line is written only after the write landed.
//   { status: 'saved', column }  |  { status: 'stale', column }  |  { status: 'error', error }
export async function saveColumnLinks({ bookId, title, openedWith, draft }) {
  const now = await supabase.from('books').select('purchase_links').eq('id', bookId).maybeSingle();
  if (now.error) return { status: 'error', error: now.error.message };
  if (!now.data) return { status: 'error', error: LINK_WORDS.noSuchBook };
  if (!same(asList(now.data.purchase_links), openedWith)) {
    return { status: 'stale', column: asLinks(now.data.purchase_links), rawColumn: asList(now.data.purchase_links) };
  }
  const clean = asLinks(draft).map(trimmed);
  const { data, error } = await supabase.from('books')
    .update({ purchase_links: clean })
    .eq('id', bookId)
    .select('id, purchase_links');
  if (error) return { status: 'error', error: error.message };
  if (!data?.length) return { status: 'error', error: 'the database changed nothing' };
  await logAudit('update', 'book', saveAuditText(title, bookId, asLinks(openedWith).map(trimmed), clean), bookId);
  return { status: 'saved', column: asLinks(data[0].purchase_links), rawColumn: asList(data[0].purchase_links) };
}

// Remove one link from the table. The delete asks for the row back, and an empty result is a
// failure (the database refuses an admin's delete only if the policy is missing). The audit
// line is written only after the row is confirmed gone.
//   { error: null }  |  { error: 'message' }
export async function removeTableLink({ bookId, title, link }) {
  const { data, error } = await supabase.from('purchase_links').delete().eq('id', link.id).select('id');
  if (error) return { error: error.message };
  if (!data?.length) return { error: 'the database changed nothing' };
  const text = `Buy link removed from "${title}" (book ${bookId}): ${pair(link)}`;
  await logAudit('delete', 'book', text.length > 600 ? `${text.slice(0, 597)}...` : text, bookId);
  return { error: null };
}
